<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Draggable from 'dashboard/components/widgets/webphone/Draggable.vue';
import { useWacallsCall } from 'dashboard/composables/useWacallsCall';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import SoundRinging from 'dashboard/assets/audio/ring.mp3';

const { t } = useI18n();
const store = useStore();

// Wrap the raw getters in local computeds so the template can auto-unwrap
// them as proper refs (useMapGetter returns a computed but the value must flow
// through a real Vue ref for v-if / text interpolation to work correctly).
const incomingGetter = useMapGetter('wacallsCall/getIncoming');
const isRingingGetter = useMapGetter('wacallsCall/getIsRinging');
const incoming = computed(() => incomingGetter.value);
const isRinging = computed(() => !!isRingingGetter.value);

const { answerInbound } = useWacallsCall();

// --- ringtone ---
let ringSound = null;

const stopRingtone = () => {
  if (!ringSound) return;
  ringSound.pause();
  ringSound.currentTime = 0;
  ringSound = null;
};

const startRingtone = () => {
  stopRingtone();
  ringSound = new Audio(SoundRinging);
  ringSound.loop = true;
  ringSound.play?.().catch(() => {});
};

watch(
  isRinging,
  ringing => {
    if (ringing) startRingtone();
    else stopRingtone();
  },
  { immediate: true }
);

// --- actions ---
const answering = ref(false);

const onAnswer = async () => {
  if (answering.value) return; // guard against double-click / double-trigger
  answering.value = true;
  // Snapshot before any dispatch clears the store state.
  const { conversationId, callId, contactName, phone } = incoming.value;
  try {
    await answerInbound(conversationId, callId);
    store.dispatch('wacallsCall/clearIncoming');
    store.dispatch('wacallsCall/setActiveCall', {
      conversationId,
      callId,
      contactName,
      phone,
    });
    store.dispatch('wacallsCall/setStatus', 'active');
  } catch (e) {
    store.dispatch('wacallsCall/clearIncoming');
    // 409 = another agent already answered (first-wins) — not an error.
    const key =
      e.status === 409
        ? 'CONVERSATION.WACALLS_CALL.ANSWERED_ELSEWHERE'
        : 'CONVERSATION.WACALLS_CALL.ERROR_GENERIC';
    useAlert(t(key));
  } finally {
    answering.value = false;
  }
};

const onDecline = () => {
  store.dispatch('wacallsCall/clearIncoming');
};

// --- emitter handlers (named so they can be unregistered exactly) ---
function onCallTerminated() {
  store.dispatch('wacallsCall/clearIncoming');
}

onMounted(() => {
  emitter.on('wacalls_call:terminated', onCallTerminated);
});

onUnmounted(() => {
  emitter.off('wacalls_call:terminated', onCallTerminated);
  stopRingtone();
});
</script>

<template>
  <Draggable v-if="isRinging" :initial-x="50" :initial-y="120">
    <div
      data-testid="wacalls-incoming-call-toast"
      class="flex flex-col w-[280px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-lg select-none gap-3"
    >
      <!-- Contact info -->
      <div class="flex flex-col gap-0.5">
        <p
          class="text-sm font-semibold text-slate-800 dark:text-slate-100 m-0 truncate"
        >
          {{ incoming.contactName || incoming.phone }}
        </p>
        <p class="text-xs text-slate-500 dark:text-slate-400 m-0">
          {{ $t('CONVERSATION.WACALLS_CALL.INCOMING_TITLE') }}
        </p>
      </div>

      <!-- Action buttons -->
      <div class="flex gap-2 justify-end">
        <button
          data-testid="wacalls-decline-btn"
          class="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300 text-xs font-medium hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors"
          @click="onDecline"
        >
          <span class="i-lucide-phone-off text-sm" />
          {{ $t('CONVERSATION.WACALLS_CALL.DECLINE') }}
        </button>
        <button
          data-testid="wacalls-answer-btn"
          :disabled="answering"
          class="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-500 text-white text-xs font-medium hover:bg-green-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          @click="onAnswer"
        >
          <span class="i-ri-phone-fill text-sm" />
          {{ $t('CONVERSATION.WACALLS_CALL.ANSWER') }}
        </button>
      </div>
    </div>
  </Draggable>
  <template v-else />
</template>
